import { useNavigate } from "infrastructure/router";
import { BooleanSwitchValue } from "consts/general";
import { WebRoutes } from "consts/enpoints/web";
import { delay } from "util/support";
import { putOnboardingsApplications } from "features/ecommerce/api";
import useActions from "features/auth/useActions";
import type { OnboardingApplication, WithRoute } from "features/ecommerce/types";
import { CompleteOnboarding } from "ui/forms/ECommerce";
import { useOnboardingForm } from "ui/organizms/ECommerce/Onboarding/common";

type UseCompleteArg = {
    readonly route: WithRoute;
    readonly getApplicationModel: () => Map<number, {
        readonly index: number;
        readonly isIncomplete: boolean;
        readonly hasRequestedChange: boolean;
        readonly countable?: boolean;
        readonly layout?: {
            readonly stepContainerSize: string | number;
        };
    }>;
};

export const useOnboardingComplete = <TApplication extends object>({ route, getApplicationModel }: UseCompleteArg) => {
    const navigate = useNavigate();
    const { getUser } = useActions();

    const withValues = useOnboardingForm({
        save: putOnboardingsApplications<TApplication>,
        onSuccess: () => {
            const [request] = getUser();

            return request
                .then(() => delay(100))
                .then(() => navigate(WebRoutes.Root));
        },
        route,
        getApplicationModel
    });

    const formProps: CompleteOnboarding.OnboardingCompleteOnboaringForm<TApplication> = {
        bootstrapData: {},
        initialValues: CompleteOnboarding.getInitialValues<TApplication>({}),
        isBootstrapDataLoading: false,
        onSaveOrCreate: (_, formHelpers) => withValues({
            hasAgreedTermsConditions: BooleanSwitchValue.On
        } as OnboardingApplication<TApplication>, formHelpers)
    };

    return {
        ...formProps,
        ...CompleteOnboarding.useForm(formProps)
    };
};

export default useOnboardingComplete;
